import { Card, CardBody, Col, Nav, NavItem, Row, NavLink } from "reactstrap";
import classnames from 'classnames';
import { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "slices/store";
import { useQuery } from "@tanstack/react-query";
import { getRentAgreementPropertiesListingByUserId, getSellOfferedPropertiesListingByUserId } from "endpoints/users/userDetailPage.action";
import SellPropertiesListing from "./SellPropertiesListing";
import RentPropertiesListing from "./RentPropertiesListing";

const PropertiesTabs = () => {
    const [activeTab, setActiveTab] = useState('sell');
    const [sellPropertiesCurrentPage, setSellPropertiesCurrentPage] = useState(1);
    const [rentPropertiesCurrentPage, setRentPropertiesCurrentPage] = useState(1);

    const userId = useSelector((state: RootState) => state.User.selectedUserId);
    const [limit] = useState(10);

    // React Query for rent properties data
    const { 
        data: rentPropertiesResponse, 
        isLoading: isRentPropertiesLoading,
        isError: isRentPropertiesError,
        refetch: refetchRentProperties,
        isRefetching: isRentPropertiesRefetching
    } = useQuery({
        queryKey: ['rentAgreementPropertiesByUser', userId, rentPropertiesCurrentPage, limit],
        queryFn: () => getRentAgreementPropertiesListingByUserId({
            customerId: userId,
            page: rentPropertiesCurrentPage,
            limit: limit
        }),
        enabled: !!userId && activeTab === 'rent',
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        refetchOnReconnect: true,
        retry: 3,
    });

    const rentProperties = rentPropertiesResponse?.data?.results || [];
    const rentPropertiesTotalCount = rentPropertiesResponse?.data?.total || 0;
    const rentPropertiesTotalPages = Math.ceil(rentPropertiesTotalCount / limit);

    // React Query for sell properties data
    const { 
        data: propertiesResponse, 
        isLoading: isPropertiesLoading, 
        isError: isPropertiesError, 
        refetch: refetchProperties,
        isRefetching: isPropertiesRefetching
    } = useQuery({
        queryKey: ['sellOfferedPropertiesByUser', userId, sellPropertiesCurrentPage, limit],
        queryFn: () => getSellOfferedPropertiesListingByUserId({
            customerId: userId,
            page: sellPropertiesCurrentPage,
            limit: limit
        }),
        enabled: !!userId && activeTab === 'sell',
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        refetchOnReconnect: true,
        retry: 3,
    });

    const sellProperties = propertiesResponse?.data?.results || [];
    const sellPropertiesTotalCount = propertiesResponse?.data?.total || 0;
    const sellPropertiesTotalPages = Math.ceil(sellPropertiesTotalCount / limit);

    const handleSellPropertiesPageChange = (page: number) => {
        setSellPropertiesCurrentPage(page);
    };

    const handleRentPropertiesPageChange = (page: number) => {
        setRentPropertiesCurrentPage(page);
    }

    const handleTabChange = (tab: string) => {
        setActiveTab(tab);
        // Reset pagination when switching tabs
        if (tab === 'sell') {
            setSellPropertiesCurrentPage(1);
        } else if (tab === 'rent') {
            setRentPropertiesCurrentPage(1);
        }
    };

    return (
        <Card>
            <CardBody>
                <Row className="mb-3">
                    <Col md={6} >
                        <Nav tabs className="nav nav-tabs nav-tabs-custom nav-success nav-justified mb-0">
                            <NavItem>
                                <NavLink
                                    className={classnames({ active: activeTab === 'sell' })}
                                    onClick={() => handleTabChange('sell')}
                                    style={{ cursor: 'pointer' }}>
                                    Sell
                                    {activeTab === 'sell' && sellPropertiesTotalCount > 0 && (
                                        <span className="badge bg-success ms-2">{sellPropertiesTotalCount}</span>
                                    )}
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    className={classnames({ active: activeTab === 'rent' })}
                                    onClick={() => handleTabChange('rent')}
                                    style={{ cursor: 'pointer' }}>
                                    Rent
                                    {activeTab === 'rent' && rentPropertiesTotalCount > 0 && (
                                        <span className="badge bg-success ms-2">{rentPropertiesTotalCount}</span>
                                    )}
                                </NavLink>
                            </NavItem>
                        </Nav>
                    </Col>
                </Row>
                <Row>
                    {activeTab === 'sell' && (
                        <SellPropertiesListing
                            data={sellProperties}
                            loading={isPropertiesLoading}
                            isError={isPropertiesError}
                            currentPage={sellPropertiesCurrentPage}
                            totalPages={sellPropertiesTotalPages}
                            totalCount={sellPropertiesTotalCount}
                            onPageChange={handleSellPropertiesPageChange}
                            onRefresh={refetchProperties}
                            isRefetching={isPropertiesRefetching}
                        />
                    )}
                    {activeTab === 'rent' && (
                        <RentPropertiesListing
                            data={rentProperties}
                            loading={isRentPropertiesLoading}
                            isError={isRentPropertiesError}
                            currentPage={rentPropertiesCurrentPage}
                            totalPages={rentPropertiesTotalPages}
                            totalCount={rentPropertiesTotalCount}
                            onPageChange={handleRentPropertiesPageChange}
                            onRefresh={refetchRentProperties}
                            isRefetching={isRentPropertiesRefetching}
                        />
                    )}
                </Row>
            </CardBody>
        </Card>
    );
};
export default PropertiesTabs;